/**
 * -----------------------------------------------------------------------------
 * Crash Track Listing Page
 * -----------------------------------------------------------------------------
 * This page is used to list all the crash details
 */

import { fromZonedTime } from "date-fns-tz";
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { DateRange } from "rsuite/esm/DateRangePicker";
import { useDebouncedCallback } from "use-debounce";
import { useCrashTrackQuery } from "../../api/network/adminApiServices";
import {
  TListingQueryParams,
  setListingQueryParams,
} from "../../api/store/commonSlice";
import { TCrashTrackRecord } from "../../api/types/CrashTrack";
import { TSelectboxOption } from "../../components/admin/formFields";
import HeaderView from "../../components/admin/headerView";
import AdminTable from "../../components/adminTable";
import ListingPageSubHeader from "../../components/listingPageSubHeader";
import ListingTableHeader from "../../components/listingTableHeader";
import {
  EditListingColumnsButton,
  ModalEditListingColumns,
  TListingColumn,
} from "../../components/modalEditListingColumns";
import Pagination, { TPaginationSelected } from "../../components/pagination";
import { TAdminTableRowData } from "../../components/types";
import { APP_CONFIG } from "../../constants/constants";
import { ACL_NAMES, useACL } from "../../hooks/acl";
import { useAccountTimezone } from "../../hooks/timezone";
import { routeUrls } from "../../navigation/routeUrls";
import { SortingStatusWithReset } from "../../utils/adminListings";
import { createCrashTrackUriChunk } from "../../utils/crashTrack";
import {
  dbTimeToJsTime,
  formattedDateTimeBasedOnTimezone,
  jsTimeToDBTime,
} from "../../utils/dateTime";
import {
  localStorageKeys,
  useLocalStorage,
} from "../../utils/localStorageItems";
import { useLoggedInUserData } from "../../utils/user";
import { ROLE_ACCESS_LEVELS } from "../admin-details/role/constants";
import FormFieldDateRangePicker from "../camera-admin/video-history/dateRangePicker";

const columnOrderingFields: Record<string, string> = {
  "Asset ID": "deviceid",
  "Asset Description": "description",
  "Display Name": "displayname",
  Driver: "drivername",
  Make: "vehiclemake",
  Model: "vehiclemodel",
  VIN: "vehiclevin",
  Groups: "groups",
  IMEI: "imeinumber",
  "Event Date and Time": "crash_event.timestamp",
  "G Force": "g_force",
  // "Event Address": "" // intentionally excluded from sortability
};

const ScreenDashboardAdminCrashTrack = () => {
  const { t } = useTranslation("translation", {
    keyPrefix: "admins.crashtrack",
  });
  const navigate = useNavigate();

  const thisOrgDriverAllowed = useLoggedInUserData("hasdrivers");
  const showDriver = thisOrgDriverAllowed === "1";

  const all_columns = [
    "Asset ID",
    "Asset Description",
    "Display Name",
    ...(showDriver ? ["Driver"] : []),
    "Make",
    "Model",
    "VIN",
    "Groups",
    "IMEI",
    "Event Date and Time",
    "G Force",
    "Event Address",
  ];

  const thisOrgCrashtrackEnabled = useLoggedInUserData("crashtrackEnabled");
  const { getUserACLAccessLevel } = useACL();
  useEffect(() => {
    if (
      thisOrgCrashtrackEnabled !== "1" &&
      getUserACLAccessLevel(ACL_NAMES.ACL_ADMIN_CRASHTRACK) <
        ROLE_ACCESS_LEVELS.READ
    ) {
      navigate(routeUrls.dashboard);
    }
  }, []);

  const listingQueryParams: TListingQueryParams = useSelector(
    (state: any) => state.commonReducer.listingQueryParams
  );
  const { crashTrack: crashTrackQueryParams } = listingQueryParams;
  const dispatch = useDispatch();
  const listingWrapperRef = React.useRef<HTMLDivElement>(null);

  // columns
  const { getLocalStorageItem } = useLocalStorage();
  const savedColumns = getLocalStorageItem(localStorageKeys.columns.CrashTrack);
  const [columns, setColumns] = React.useState<TListingColumn[]>(
    all_columns.map((colItem, _) => {
      return {
        name: colItem,
        show: !!savedColumns ? savedColumns.includes(colItem) : true,
      };
    })
  );

  // retrieve account timezone
  const { tzForDateFns, convertToTZForDateFns } = useAccountTimezone();

  // preparing query params
  const debouncedSetSearchKeyword = useDebouncedCallback((value: string) => {
    dispatch(
      setListingQueryParams({
        ...listingQueryParams,
        crashTrack: {
          ...crashTrackQueryParams,
          page: 1,
          search: value,
        },
      })
    );
  }, 500);

  const handleOrderingChange = (ordering?: string) => {
    dispatch(
      setListingQueryParams({
        ...listingQueryParams,
        crashTrack: {
          ...crashTrackQueryParams,
          ordering,
          page: 1,
        },
      })
    );
  };

  // handler for the date range picker
  const handleRangeChange = (value: [any, any] | null) => {
    if (value) {
      const [start, end] = value;
      if (!start || !end || isNaN(start.getTime()) || isNaN(end.getTime())) {
        // Invalid date detected, abort further logic
        return;
      }
      const utcStartDate = fromZonedTime(
        start,
        convertToTZForDateFns(tzForDateFns)
      );
      const jsTimeStart = new Date(utcStartDate).getTime();
      const utcEndDate = fromZonedTime(
        end,
        convertToTZForDateFns(tzForDateFns)
      );
      const jsTimeEnd = new Date(utcEndDate).getTime();
      dispatch(
        setListingQueryParams({
          ...listingQueryParams,
          crashTrack: {
            ...crashTrackQueryParams,
            start_time: jsTimeToDBTime(jsTimeStart),
            end_time: jsTimeToDBTime(jsTimeEnd),
            page: 1,
          },
        })
      );
    }
  };

  // fetching crash track data
  const { data: dataCrashTrackList, isFetching: isFetchingCrashTrackList } =
    useCrashTrackQuery(
      {
        ...crashTrackQueryParams,
        // 'search_fields' intentionally not added here as default search fields are defined in the API service
      },
      {
        skip:
          crashTrackQueryParams.start_time === null ||
          crashTrackQueryParams.end_time === null,
      }
    );
  const { count, next, previous, results } = dataCrashTrackList || {};

  // smooth scroll listing to the top on page change
  useEffect(() => {
    listingWrapperRef?.current?.scrollTo({ behavior: "smooth", top: 0 });
  }, [dataCrashTrackList]);

  // preparing table data
  const tableData: TAdminTableRowData[] = !!results
    ? (results || ([] as TCrashTrackRecord[])).map(
        (item: TCrashTrackRecord, index: number) => ({
          navLink: `${routeUrls.dashboardChildren.crashTrack}/${createCrashTrackUriChunk(item.deviceid, item?.crash_event?.timestamp)}`,
          cellData: [
            // listingQueryParams.users.page_size * (listingQueryParams.users.page - 1) + index + 1, // sr_no by page
            item?.deviceid, // Asset ID
            item?.description || "-", // Vehicle
            item?.displayname || "-",
            ...(showDriver ? [item?.drivername ?? "-"] : []), // Driver Name
            item?.vehiclemake || "-", // Make
            item?.vehiclemodel || "-", // Model
            item?.vehiclevin, // VIN
            item?.groups || "-", // Groups
            item?.imeinumber, // IMEI
            !!item?.crash_event?.timestamp && !!tzForDateFns
              ? formattedDateTimeBasedOnTimezone(
                  dbTimeToJsTime(item?.crash_event.timestamp),
                  tzForDateFns
                )
              : "-", // Event Date and Time
            item?.g_force || "-", // G Force
            item?.crash_event?.event_address || "-", // Event Address
          ].filter((_, index) => columns[index].show),
          isActive: true, // item?.isactive === 1,
        })
      )
    : [];

  // rendering
  return (
    <>
      <HeaderView title={t("heading")} />
      <div className={`${APP_CONFIG.DES.DASH.P_HORIZ} pt-4`}>
        <ListingPageSubHeader heading={t("sub_heading")} />
        <ListingTableHeader
          heading={
            <div className="flex items-center gap-4">
              {/* Heading */}
              <span>{t("listing_heading")}</span>

              {/* Sorted by label */}
              {!!crashTrackQueryParams?.ordering && (
                <SortingStatusWithReset
                  ordering={crashTrackQueryParams.ordering}
                  columnOrderingFields={columnOrderingFields}
                  onResetOrdering={() => handleOrderingChange(undefined)}
                />
              )}
            </div>
          }
          searchBoxPlaceholder={t("search_placeholder")}
          searchBoxValue={crashTrackQueryParams.search}
          searchBoxOnChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            debouncedSetSearchKeyword(e.target.value)
          }
          searchBoxOnClear={() => debouncedSetSearchKeyword("")}
          rightWrapperClass="flex flex-col md:grid md:grid-cols-1 md:grid-cols-[3fr_2fr] gap-4 w-full lg:w-[560px] max-w-full"
          BesideSearchField={
            <FormFieldDateRangePicker
              className="flex h-11"
              value={
                crashTrackQueryParams?.start_time &&
                crashTrackQueryParams?.end_time
                  ? ([
                      new Date(
                        formattedDateTimeBasedOnTimezone?.(
                          dbTimeToJsTime(crashTrackQueryParams?.start_time),
                          tzForDateFns,
                          APP_CONFIG.DATETIME_FORMATS.DISPLAY.DATE_TIME
                        )
                      ),
                      new Date(
                        formattedDateTimeBasedOnTimezone?.(
                          dbTimeToJsTime(crashTrackQueryParams?.end_time),
                          tzForDateFns,
                          APP_CONFIG.DATETIME_FORMATS.DISPLAY.DATE_TIME
                        )
                      ),
                    ] as DateRange)
                  : null
              }
              onChange={handleRangeChange}
            />
          }
        />
        <ModalEditListingColumns
          columns={columns}
          setColumns={setColumns}
          lsKey={localStorageKeys.columns.CrashTrack}
        />
        <div className="py-4 mt-4 relative">
          <EditListingColumnsButton />
          <AdminTable
            viewportHeightOffset={"400"}
            columns={columns
              .filter((item) => item.show)
              .map((item) => item.name)}
            data={tableData}
            isLoading={isFetchingCrashTrackList}
            listingQueryParams={crashTrackQueryParams}
            listingWrapperRef={listingWrapperRef}
            onOrderingChange={handleOrderingChange}
            ordering={crashTrackQueryParams.ordering}
            sortableColumns={columnOrderingFields}
          />
          {!isFetchingCrashTrackList && (
            <Pagination
              pageSize={crashTrackQueryParams.page_size}
              handlePageSizeChange={(e: TSelectboxOption | null) => {
                dispatch(
                  setListingQueryParams({
                    ...listingQueryParams,
                    crashTrack: {
                      ...crashTrackQueryParams,
                      page: 1,
                      page_size: parseInt(`${e?.value}`),
                    },
                  })
                );
              }}
              totalPages={
                count ? Math.ceil(count / crashTrackQueryParams.page_size) : 1
              }
              forcePage={crashTrackQueryParams.page - 1}
              handlePageClick={(data: TPaginationSelected) => {
                dispatch(
                  setListingQueryParams({
                    ...listingQueryParams,
                    crashTrack: {
                      ...crashTrackQueryParams,
                      page: data?.selected + 1,
                    },
                  })
                );
              }}
              paginCurrentPage={crashTrackQueryParams.page}
              paginItemsOnCurrentPage={results?.length || 0}
              paginTotalItemsCount={count}
            />
          )}
        </div>
      </div>
    </>
  );
};

export default ScreenDashboardAdminCrashTrack;
